/**
 * Shared vehicle edit/update helpers for appointment-details-modal and schedule-appointment-modal.
 */
import type { UpdateVehiclePayload } from '../../services/vehicle.service';
import { sanitizeErrorMessage } from './error.util';

/** Shape used to build update payload; matches EditVehicleData from vehicle-list.component. */
export interface EditVehicleDataLike {
  id: string;
  licensePlate: string;
  state: string;
  vehicleData: { vin?: string; make?: string; model?: string; year?: number };
}

export function buildVehicleUpdatePayload(updatedData: EditVehicleDataLike): UpdateVehiclePayload {
  return {
    license_plate: updatedData.licensePlate,
    state: updatedData.state,
    vin: updatedData.vehicleData?.vin ?? '',
    make: updatedData.vehicleData?.make ?? '',
    model: updatedData.vehicleData?.model ?? '',
    year: updatedData.vehicleData?.year ?? 0
  };
}

export interface VehicleUpdateCallbacks {
  onSuccess(response: { status: boolean; message?: string }, updatedData: EditVehicleDataLike): void;
  onError?(error: any): void;
}

export interface NotificationServiceLike {
  add(msg: { severity?: string; summary?: string; detail?: string; [key: string]: any }): void;
}

/**
* Update a vehicle by validating input, building an update payload, calling the provided vehicle service, and handling success/error responses with messaging and callbacks.
* @example
* runVehicleUpdate({ updateVehicle: (id, payload) => import('rxjs').of({ status: true }) }, { id: 'vehicle-123' }, { add: msg => console.log(msg) }, { onSuccess: (res, data) => {}, onError: err => {} })
* undefined
* @param {{ updateVehicle(id: string, payload: UpdateVehiclePayload): import('rxjs').Observable<{ status: boolean; message?: string }> }} {{vehicleService}} - Service exposing updateVehicle to send the update request and return an observable of the response.
* @param {{EditVehicleDataLike}} {{updatedData}} - Data used to build the update payload; must include an id property or the update will be aborted.
* @param {{NotificationServiceLike}} {{messageService}} - Messaging service used to display success or error notifications to the user.
* @param {{VehicleUpdateCallbacks}} {{callbacks}} - Callback handlers invoked on success (onSuccess) and on error (onError, optional).
* @returns {{void}} Void - This function performs side effects and does not return a value.
*/
export function runVehicleUpdate(
  vehicleService: { updateVehicle(id: string, payload: UpdateVehiclePayload): import('rxjs').Observable<{ status: boolean; message?: string }> },
  updatedData: EditVehicleDataLike,
  messageService: NotificationServiceLike,
  callbacks: VehicleUpdateCallbacks
): void {
  if (!updatedData.id) {
    messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: sanitizeErrorMessage('Vehicle ID is missing. Cannot update vehicle.')
    });
    return;
  }
  const payload = buildVehicleUpdatePayload(updatedData);
  vehicleService.updateVehicle(updatedData.id, payload).subscribe({
    next: (response) => {
      if (response.status) {
        callbacks.onSuccess(response, updatedData);
      } else {
        messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: sanitizeErrorMessage(response.message || 'Failed to update vehicle. Please try again.')
        });
      }
    },
    error: (error) => {
      const errorMessage = error?.error?.message || error?.message || 'Failed to update vehicle. Please try again.';
      messageService.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: sanitizeErrorMessage(errorMessage)
      });
      callbacks.onError?.(error);
    }
  });
}
